import { StatusPill } from '@/components/Rows'
import type {
	WhiteningActionId,
	WhiteningCard,
	WhiteningPill,
} from '@/features/whitening/state'
import { KIT_CONTENTS } from '@/features/whitening/state'
import { CardGroundProvider, useGroundColor } from '@/ui/core/card-ground'
import { Header } from '@/ui/core/header'
import { Ledger, numbered } from '@/ui/core/ledger'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { Image } from 'expo-image'
import { View } from 'react-native'
import Images from '#/assets/images/image-map'
import { CardSlot } from './CardSlot'

/**
 * The coral the product shot is photographed on, sampled from the file. Not a brand colour and
 * not in the palette: it is this one photograph's ground, and it is here so the block around a
 * contained picture is the same coral as the picture itself — which is what lets the picture end
 * partway down the block without a seam.
 */
const PHOTO_GROUND = '#FE7D70'

/** The card's padding, and the radius that keeps the picture's curve parallel to the card's 24. */
const CARD_PAD = 12
const PHOTO_RADIUS = 24 - CARD_PAD

/** The photograph's block. Taller than the type needs, so the picture has somewhere to be. */
const PHOTO_HEIGHT = 200

/**
 * What the eyebrow and the title come to at the block's foot, plus the air above them.
 *
 * The type is set into this band and the picture is kept out of it, which is the only way the two
 * can share 200 points. See `PICTURE_BAND`.
 */
const TYPE_BAND = 76

/**
 * The part of the block the picture gets, and why it is not the whole of it.
 *
 * The product shot is a centred still life: a lid, a tray and a tin stacked down the middle of a
 * square, together filling three quarters of its height. Covered across a 200pt landscape
 * window, no `contentPosition` clears the type — the subject is simply taller than the room
 * above the title, and every vertical offset puts one of the three pieces behind the words.
 *
 * So the picture is given the band above the type rather than the whole block, and covered
 * inside that. The crop lands on the lid, whole, with coral under it; the cut at the band's
 * foot is invisible because what continues below it is the same coral the lid was photographed
 * on.
 */
const PICTURE_BAND = PHOTO_HEIGHT - TYPE_BAND

/**
 * The widest the picture may render, which is what keeps the lid whole on a wide screen.
 *
 * Cover scales to the width, so a wider card shows less of the photograph's height: past about
 * 346 points the band stops being deep enough to hold the lid and starts slicing its base. On a
 * phone this never binds — the card is narrower than the cap — and on the desktop column the
 * picture centres on the coral instead of growing into a crop.
 */
const PICTURE_MAX_WIDTH = 340

/** What the words are set in past the picture, which carries the card's own padding instead. */
const TEXT_PAD = 14

/**
 * The air between the photograph and the ledger. The largest gap on the card, deliberately: the
 * title names the thing and the list is a different subject under it.
 */
const TITLE_TO_LIST = 24

/**
 * The state, on the picture's top corner: one badge, one treatment, whatever it says.
 *
 * Three treatments stood here for a while — a coral tag for the claim, grey while the kit was
 * coming, mint with a green check once it arrived — and because a member meets one of them at a
 * time, weeks apart, they read as three kinds of object in one corner rather than as one thing
 * changing. The mark still moves with the state; the colour does not.
 *
 * It stands on its own white ground, which is why it needed nothing from the fade that used to
 * be under it: the type inside it is ink on white however bright the coral behind it gets, and
 * with the rest of the block now ink on coral the pill is the only white shape in it.
 */
const Pill = ({ pill }: { pill: WhiteningPill }) => (
	<StatusPill
		status={pill.status}
		label={pill.label}
		glyph={pill.glyph}
		neutral
		onGround
	/>
)

/**
 * The whitening kit, as one card in every state.
 *
 * The photograph with the type on it, the contents as a ledger, and one slot at the foot. Only
 * the pill and that slot move: a member opening this page in March and again in May meets the
 * same object with a different last line, rather than two screens that happen to share a
 * picture.
 *
 * The contents stay the contents throughout. They were swapped for a set of instructions at
 * delivery for a while, and then the instructions moved to a sheet behind a link — both of which
 * were the app answering a question the box answers better, since the printed guide is in the
 * member's hand while they are holding the trays. Item 03 says where it is.
 *
 * White, and it says so out loud rather than leaning on the context's default, which is blush —
 * a default is what an unwrapped row assumes, not what an unremarkable card is. Two components
 * for the same reason the gel card has two: a provider cannot be its own consumer, so this one
 * declares the ground and the body reads every colour off it.
 */
export const KitCard = (props: {
	card: WhiteningCard
	onAction: (action: WhiteningActionId) => void
	onMessageUs: () => void
}) => (
	<CardGroundProvider ground="white">
		<KitCardBody {...props} />
	</CardGroundProvider>
)

const KitCardBody = ({
	card,
	onAction,
	onMessageUs,
}: {
	card: WhiteningCard
	onAction: (action: WhiteningActionId) => void
	onMessageUs: () => void
}) => (
	<View
		className="rounded-note"
		style={{ padding: CARD_PAD, backgroundColor: useGroundColor() }}
	>
		<View
			className="overflow-hidden"
			style={{
				height: PHOTO_HEIGHT,
				borderRadius: PHOTO_RADIUS,
				backgroundColor: PHOTO_GROUND,
			}}
		>
			<View
				className="w-full self-center overflow-hidden"
				style={{ height: PICTURE_BAND, maxWidth: PICTURE_MAX_WIDTH }}
			>
				<Image
					source={Images.Product}
					style={{ width: '100%', height: '100%' }}
					contentFit="cover"
					// Anchored to the top of the square, which is where the lid is.
					contentPosition={{ top: '0%', left: '50%' }}
					transition={200}
				/>
			</View>
			<View
				style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: TEXT_PAD }}
			>
				{/* Set in ink, with no fade under it.

				    A dark fade is the right instinct over a photograph and the wrong one here,
				    because below the lid this block is not a photograph — it is flat coral, and
				    black over coral is brown, so the card's lower half read as mud. Ink needs no
				    fade to begin with: white on this coral measures about 2.4:1, under the 3:1
				    large type is meant to clear, while ink is nearer 8:1. It is also the
				    photograph's own treatment, since the tin in the shot carries black WALLY on
				    exactly this coral — and it leaves the pill's white ground as the only white in
				    the block, which is what makes the pill the one thing that changes.

				    Neither of these lines ever changes. They say what the card is; the pill says
				    where it has got to, which is one place rather than two. */}
				<Text size="body3" className="tracking-[0.08em]">
					{card.eyebrow}
				</Text>
				<Header
					variant="h3"
					className="uppercase"
					style={{ fontSize: 28, lineHeight: 27, marginTop: 6 }}
				>
					{card.title}
				</Header>
			</View>
			{card.pill && (
				<View style={{ position: 'absolute', top: 11, right: 12 }}>
					<Pill pill={card.pill} />
				</View>
			)}
		</View>

		<View style={{ padding: TEXT_PAD, paddingTop: TITLE_TO_LIST }}>
			<VStack gap={20}>
				{/* The membership's ledger with its numerals set large: on a sheet they are an index
				    to a list somebody is reading, and here they are the only ornament the card has. */}
				<Ledger items={KIT_CONTENTS} mark={numbered} scale="display" />
				<CardSlot slot={card.slot} onAction={onAction} />
				{/* The only thing that ever sits under the slot, and only once the kit is home.
				    Quiet, centred, and a sentence rather than a control: it is not a step in
				    anything, it is the way out if the box was wrong.

				    Only the last two words are pressable, which is what the underline is for —
				    and they take the default ink while the question stays soft, so the half of
				    the line that does something is the half that looks like it. */}
				{!!card.footnote && (
					<Text size="body2" tone="soft" className="text-center">
						{card.footnote.text}{' '}
						<Text
							size="body2"
							weight="bold"
							underlined
							accessibilityRole="link"
							onPress={onMessageUs}
						>
							{card.footnote.link}
						</Text>
					</Text>
				)}
			</VStack>
		</View>
	</View>
)
